import React, { useState, useEffect } from 'react';
import { Container, Paper, Typography, Button, Box, Chip, Alert, CircularProgress, TextField, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Card, CardContent, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Drawer, ListItemButton, useMediaQuery, useTheme } from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PolicyIcon from '@mui/icons-material/Policy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import MenuIcon from '@mui/icons-material/Menu';
import DescriptionIcon from '@mui/icons-material/Description';
import ContactsIcon from '@mui/icons-material/Contacts';
import ArticleIcon from '@mui/icons-material/Article';
import EmailIcon from '@mui/icons-material/Email';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import api from '../api';

function DisputeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedDraft, setEditedDraft] = useState('');
  const [escalating, setEscalating] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [amountRecovered, setAmountRecovered] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [prediction, setPrediction] = useState(null);
  const [showPrediction, setShowPrediction] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchDispute = async () => {
    try {
      const response = await api.get(`/disputes/${id}/`);
      setDispute(response.data);
      setEditedDraft(response.data.user_edited_draft || response.data.ai_draft);
      // Set recipient email from company contacts if available
      if (response.data.company_contacts?.email && 
          response.data.company_contacts.email !== 'null' && 
          response.data.company_contacts.email !== null) {
        setRecipientEmail(response.data.company_contacts.email);
      }
    } catch (err) {
      setError('Failed to load letter');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    const textToCopy = dispute.user_edited_draft || dispute.ai_draft;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    fetchDispute();
    const stored = localStorage.getItem('userInfo');
    if (stored) setUserInfo(JSON.parse(stored));
    
    // Get prediction from navigation state if available
    if (location.state?.prediction) {
      setPrediction(location.state.prediction);
      setShowPrediction(location.state.showPrediction || false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleEscalate = async () => {
    if (!window.confirm(
      'Escalate this letter?\n\n' +
      'We will generate BBB complaint, FTC report, and chargeback letter and email them to you.\n\n' +
      'This is a Pro feature.'
    )) return;
    
    setEscalating(true);
    try {
      const response = await api.post(`/disputes/${id}/escalate/`);
      setDispute(response.data.dispute);
      if (response.data.email_sent) {
        alert('✓ Escalation documents generated and emailed to you! Check your inbox.');
      } else {
        alert('✓ Escalation documents generated and saved to your dispute.');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to escalate letter';
      alert(errorMsg);
    } finally {
      setEscalating(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await api.patch(`/disputes/${id}/`, { user_edited_draft: editedDraft });
      setDispute({...dispute, user_edited_draft: editedDraft});
      setEditing(false);
    } catch (err) {
      alert('Failed to save changes');
    }
  };

  const handleMarkSent = async () => {
    try {
      const response = await api.post(`/disputes/${id}/mark_sent/`);
      setDispute(response.data);
    } catch (err) {
      alert('Failed to mark as sent');
    }
  };

  const handleSendEmail = async () => {
    if (!recipientEmail) {
      alert('Please enter a company email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      await api.post(`/disputes/${id}/send_email/`, {
        to_email: recipientEmail
      });
      
      alert(`✓ Email sent successfully to ${recipientEmail}!`);
      setEmailDialogOpen(false);
      // Refresh dispute to show updated status
      fetchDispute();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to send email';
      alert(`✗ ${errorMsg}`);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleMarkResolved = async () => {
    setResolveDialogOpen(true);
  };

  const handleResolveSubmit = async () => {
    try {
      const payload = {
        resolution_notes: resolutionNotes || ''
      };
      
      // Only include amount_recovered if a value was entered
      if (amountRecovered && !isNaN(parseFloat(amountRecovered))) {
        payload.amount_recovered = parseFloat(amountRecovered);
      }
      
      const response = await api.post(`/disputes/${id}/mark_resolved/`, payload);
      setDispute(response.data);
      setResolveDialogOpen(false);
      setResolutionNotes('');
      setAmountRecovered('');
    } catch (err) {
      alert('Failed to mark as resolved');
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Regenerate AI letter? This will overwrite your current draft.')) return;
    
    try {
      const response = await api.post(`/disputes/${id}/regenerate/`);
      setDispute(response.data);
      setEditedDraft(response.data.ai_draft);
      alert('Letter regenerated!');
    } catch (err) {
      alert('Failed to regenerate letter');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !dispute) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Letter not found'}</Alert>
      </Container>
    );
  }

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (isMobile) setNavOpen(false);
    }
  };

  const navigationItems = [
    { id: 'overview', label: 'Overview', icon: <DescriptionIcon /> },
    { id: 'contacts', label: 'Contact Info', icon: <ContactsIcon />, show: dispute?.company_contacts },
    { id: 'prediction', label: 'Success Analysis', icon: <TrendingUpIcon />, show: showPrediction && prediction },
    { id: 'letter', label: 'Your Letter', icon: <ArticleIcon /> },
    { id: 'actions', label: 'Actions', icon: <SendIcon /> },
  ].filter(item => item.show !== false);

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button onClick={() => navigate('/dashboard')} sx={{ fontSize: { xs: '0.875rem', md: '0.9375rem' } }}>
          ← Back to My Letters
        </Button>
        {isMobile && (
          <Button 
            startIcon={<MenuIcon />}
            onClick={() => setNavOpen(true)}
            variant="outlined"
            size="small"
          >
            Jump to Section
          </Button>
        )}
      </Box>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={navOpen}
        onClose={() => setNavOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Jump to Section</Typography>
          <List>
            {navigationItems.map((item) => (
              <ListItemButton 
                key={item.id} 
                onClick={() => scrollToSection(item.id)}
                sx={{ 
                  borderRadius: 1,
                  mb: 0.5,
                  '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)' }
                }}
              >
                <ListItemIcon sx={{ color: 'primary.main' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Desktop Sticky Navigation */}
        {!isMobile && (
          <Paper 
            sx={{ 
              width: 240, 
              height: 'fit-content',
              position: 'sticky',
              top: 80,
              p: 2,
              display: { xs: 'none', md: 'block' }
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
              QUICK NAVIGATION
            </Typography>
            <List dense>
              {navigationItems.map((item) => (
                <ListItemButton 
                  key={item.id} 
                  onClick={() => scrollToSection(item.id)}
                  sx={{ 
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': { bgcolor: 'rgba(0, 212, 255, 0.1)' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}

        {/* Main Content */}
        <Paper sx={{ p: 4, flexGrow: 1 }} id="overview">
        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 4 }}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
              {dispute.company}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip 
                label={dispute.status.toUpperCase()} 
                color="primary" 
                sx={{ fontWeight: 'bold' }}
              />
              {dispute.amount_claimed && (
                <Chip 
                  label={`Claimed: $${parseFloat(dispute.amount_claimed).toFixed(2)}`} 
                  icon={<AttachMoneyIcon />}
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              <Typography variant="body2" color="text.secondary">
                Created {new Date(dispute.created_at).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Description Card */}
        <Card sx={{ mb: 4, border: '1px solid rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.02)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ bgcolor: 'rgba(0, 212, 255, 0.15)', borderRadius: '50%', p: 1 }}>
                <DescriptionIcon sx={{ color: 'primary.main', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Your Dispute Details
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, pl: 1 }}>
              {dispute.description}
            </Typography>
          </CardContent>
        </Card>

        {/* Company Contact Information */}
        {dispute.company_contacts && Object.keys(dispute.company_contacts).some(k => dispute.company_contacts[k]) && (
          <Card 
            id="contacts"
            sx={{ mb: 4, border: '1px solid rgba(0, 212, 255, 0.3)', bgcolor: 'rgba(0, 212, 255, 0.03)' }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box sx={{ bgcolor: 'rgba(0, 212, 255, 0.2)', borderRadius: '50%', p: 1 }}>
                  <ContactsIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Company Contact Information
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2.5 }}>
                {dispute.company_contacts.email && dispute.company_contacts.email !== 'null' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EmailIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Email
                      </Typography>
                      <Typography variant="body2">
                        <a href={`mailto:${dispute.company_contacts.email}`} style={{ color: '#00d4ff', textDecoration: 'none' }}>
                          {dispute.company_contacts.email}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
                {dispute.company_contacts.phone && dispute.company_contacts.phone !== 'null' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ContactsIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Phone
                      </Typography>
                      <Typography variant="body2">
                        {dispute.company_contacts.phone}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {dispute.company_contacts.mailing_address && dispute.company_contacts.mailing_address !== 'null' && (
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5, gridColumn: { xs: '1', md: 'span 2' } }}>
                    <ContactsIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.5 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Mailing Address
                      </Typography>
                      <Typography variant="body2">
                        {dispute.company_contacts.mailing_address}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {dispute.company_contacts.website && dispute.company_contacts.website !== 'null' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, gridColumn: { xs: '1', md: 'span 2' } }}>
                    <ContactsIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Website
                      </Typography>
                      <Typography variant="body2">
                        <a href={dispute.company_contacts.website} target="_blank" rel="noopener noreferrer" style={{ color: '#00d4ff', textDecoration: 'none' }}>
                          {dispute.company_contacts.website}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Success Prediction Card (Pro Feature) */}
        {showPrediction && prediction && (
          <>
            <Divider sx={{ my: 4 }} id="prediction" />
            <Box sx={{ position: 'relative' }}>
              <Chip 
                label="PRO ANALYSIS" 
                size="small" 
                sx={{ 
                  position: 'absolute', 
                  top: -12, 
                  right: 16, 
                  bgcolor: 'primary.main',
                  color: '#000',
                  fontWeight: 'bold',
                  zIndex: 1
                }} 
              />
              
              {/* Header Card with Success Rate */}
              <Card 
                sx={{ 
                  mb: 3,
                  border: 2, 
                  borderColor: 'primary.main',
                  background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(10, 14, 23, 0.05) 100%)',
                }}
              >
                <CardContent sx={{ pb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                    <Box sx={{ 
                      bgcolor: 'rgba(0, 212, 255, 0.15)', 
                      borderRadius: '50%', 
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <TrendingUpIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Success Prediction
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        AI-powered analysis of your dispute strength
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      textAlign: 'center',
                      bgcolor: prediction.success_chance >= 70 ? 'rgba(76, 175, 80, 0.1)' : 
                               prediction.success_chance >= 40 ? 'rgba(255, 152, 0, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                      borderRadius: 2,
                      p: 2,
                      minWidth: 120
                    }}>
                      <Typography 
                        variant="h2" 
                        sx={{ 
                          color: prediction.success_chance >= 70 ? '#4caf50' : 
                                 prediction.success_chance >= 40 ? '#ff9800' : '#f44336',
                          fontWeight: 'bold',
                          lineHeight: 1
                        }}
                      >
                        {prediction.success_chance}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold', mt: 1, display: 'block' }}>
                        {prediction.confidence} confidence
                      </Typography>
                    </Box>
                  </Box>

                  <LinearProgress 
                    variant="determinate" 
                    value={prediction.success_chance} 
                    sx={{ 
                      height: 16, 
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: prediction.success_chance >= 70 ? '#4caf50' : 
                                prediction.success_chance >= 40 ? '#ff9800' : '#f44336',
                        borderRadius: 2
                      }
                    }} 
                  />
                </CardContent>
              </Card>

              {/* Analysis Sections Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* Key Success Factors */}
                {prediction.key_factors && prediction.key_factors.length > 0 && (
                  <Card sx={{ border: '1px solid rgba(76, 175, 80, 0.3)', bgcolor: 'rgba(76, 175, 80, 0.05)' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', borderRadius: '50%', p: 1 }}>
                          <StarIcon sx={{ color: '#4caf50', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Strengths
                        </Typography>
                      </Box>
                      <List dense disablePadding>
                        {prediction.key_factors.map((factor, idx) => (
                          <ListItem key={idx} disablePadding sx={{ mb: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={factor} 
                              primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.6 } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                )}

                {/* Warnings */}
                {prediction.warnings && prediction.warnings.length > 0 && (
                  <Card sx={{ border: '1px solid rgba(255, 152, 0, 0.3)', bgcolor: 'rgba(255, 152, 0, 0.05)' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box sx={{ bgcolor: 'rgba(255, 152, 0, 0.2)', borderRadius: '50%', p: 1 }}>
                          <WarningIcon sx={{ color: '#ff9800', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Cautions
                        </Typography>
                      </Box>
                      <List dense disablePadding>
                        {prediction.warnings.map((warning, idx) => (
                          <ListItem key={idx} disablePadding sx={{ mb: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <WarningIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={warning} 
                              primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.6 } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                )}

                {/* Recommendations */}
                {prediction.recommendations && prediction.recommendations.length > 0 && (
                  <Card sx={{ border: '1px solid rgba(0, 212, 255, 0.3)', bgcolor: 'rgba(0, 212, 255, 0.05)' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box sx={{ bgcolor: 'rgba(0, 212, 255, 0.2)', borderRadius: '50%', p: 1 }}>
                          <LightbulbIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Recommendations
                        </Typography>
                      </Box>
                      <List dense disablePadding>
                        {prediction.recommendations.map((rec, idx) => (
                          <ListItem key={idx} disablePadding sx={{ mb: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <LightbulbIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText 
                              primary={rec} 
                              primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.6 } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                )}

                {/* Best Timing */}
                {prediction.best_timing && (
                  <Card sx={{ border: '1px solid rgba(0, 212, 255, 0.3)', bgcolor: 'rgba(0, 212, 255, 0.05)' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box sx={{ bgcolor: 'rgba(0, 212, 255, 0.2)', borderRadius: '50%', p: 1 }}>
                          <AccessTimeIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Optimal Timing
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                        {prediction.best_timing}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Box>

              {/* Policy Notes - Full Width */}
              {prediction.policy_notes && (
                <Card sx={{ mt: 3, border: '1px solid rgba(0, 212, 255, 0.3)', bgcolor: 'rgba(0, 212, 255, 0.05)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box sx={{ bgcolor: 'rgba(0, 212, 255, 0.2)', borderRadius: '50%', p: 1 }}>
                        <PolicyIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Company Policy Analysis
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                      {prediction.policy_notes}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </>
        )}

        {/* Upgrade prompt for free users */}
        {!userInfo?.subscription_plan || userInfo?.subscription_plan === 'free' ? (
          <>
            <Divider sx={{ my: 3 }} />
            <Card 
              sx={{ 
                border: 2, 
                borderColor: 'divider',
                background: 'rgba(255,255,255,0.02)',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 6
                }
              }}
              onClick={() => navigate('/profile')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      🔒 Unlock Success Prediction
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upgrade to Pro to see AI-powered analysis of your dispute strength, company policy insights, and recommendations to maximize your chances of success.
                    </Typography>
                  </Box>
                  <Button variant="contained" size="small">
                    Upgrade
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </>
        ) : null}

        {/* Letter Section */}
        <Card 
          id="letter"
          sx={{ mb: 4, border: '1px solid rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.02)' }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'start', sm: 'center' }, gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ bgcolor: 'rgba(0, 212, 255, 0.15)', borderRadius: '50%', p: 1 }}>
                  <ArticleIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Your AI-Generated Letter
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {!editing && (
                  <>
                    <Button 
                      size="small" 
                      startIcon={<RefreshIcon />} 
                      onClick={handleRegenerate}
                      variant="outlined"
                      sx={{ borderColor: 'primary.main', '&:hover': { bgcolor: 'rgba(0,212,255,0.1)' } }}
                    >
                      Regenerate
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => setEditing(true)}
                      variant="outlined"
                      sx={{ borderColor: 'primary.main', '&:hover': { bgcolor: 'rgba(0,212,255,0.1)' } }}
                    >
                      Edit
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            {editing ? (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={20}
                  value={editedDraft}
                  onChange={(e) => setEditedDraft(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'monospace',
                      fontSize: '0.9rem',
                      lineHeight: 1.8
                    }
                  }}
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button variant="contained" onClick={handleSaveEdit} startIcon={<CheckCircleIcon />}>
                    Save Changes
                  </Button>
                  <Button variant="outlined" onClick={() => {setEditing(false); setEditedDraft(dispute.user_edited_draft || dispute.ai_draft);}}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  bgcolor: 'rgba(0,0,0,0.3)', 
                  whiteSpace: 'pre-wrap', 
                  fontFamily: 'monospace', 
                  fontSize: '0.9rem',
                  lineHeight: 1.8,
                  border: '1px solid rgba(0,212,255,0.2)',
                  borderRadius: 1
                }}
              >
                {dispute.user_edited_draft || dispute.ai_draft}
              </Paper>
            )}
          </CardContent>
        </Card>

        {/* Actions Section */}
        <Card 
          id="actions"
          sx={{ 
            mb: 3,
            border: '2px solid rgba(0, 212, 255, 0.4)', 
            bgcolor: 'rgba(0, 212, 255, 0.05)',
            boxShadow: '0 4px 20px rgba(0, 212, 255, 0.15)'
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box sx={{ bgcolor: 'rgba(0, 212, 255, 0.2)', borderRadius: '50%', p: 1 }}>
                <SendIcon sx={{ color: 'primary.main', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Next Steps
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                onClick={handleCopy}
                color={copied ? 'success' : 'primary'}
                sx={{ 
                  minWidth: 180,
                  fontWeight: 'bold',
                  py: 1.5
                }}
              >
                {copied ? 'Copied!' : 'Copy Letter'}
              </Button>
              
              {dispute.status === 'draft' && (
                <>
                  {dispute.company_contacts?.email && (
                    <Button 
                      variant="contained" 
                      size="large"
                      startIcon={<EmailIcon />} 
                      onClick={() => setEmailDialogOpen(true)}
                      color="success"
                      sx={{ 
                        minWidth: 180,
                        fontWeight: 'bold',
                        py: 1.5
                      }}
                    >
                      Send via Email
                    </Button>
                  )}
                  <Button 
                    variant="outlined" 
                    size="large"
                    startIcon={<SendIcon />} 
                    onClick={handleMarkSent}
                    sx={{ 
                      minWidth: 180,
                      borderColor: 'primary.main',
                      fontWeight: 'bold',
                      py: 1.5,
                      '&:hover': { bgcolor: 'rgba(0,212,255,0.1)' }
                    }}
                  >
                    Mark as Sent
                  </Button>
                </>
              )}
              
              {dispute.status === 'sent' && (
                <>
                  <Button 
                    variant="contained" 
                    size="large"
                    color="success" 
                    onClick={handleMarkResolved}
                    startIcon={<CheckCircleIcon />}
                    sx={{ 
                      minWidth: 180,
                      fontWeight: 'bold',
                      py: 1.5
                    }}
                  >
                    Mark as Resolved
                  </Button>
                  
                  {userInfo?.subscription === 'pro' && !dispute.escalated_at && (
                    <Button 
                      variant="outlined" 
                      size="large"
                      startIcon={escalating ? <CircularProgress size={20} /> : <TrendingUpIcon />}
                      onClick={handleEscalate}
                      disabled={escalating}
                      color="warning"
                      sx={{ 
                        minWidth: 180,
                        fontWeight: 'bold',
                        py: 1.5,
                        borderWidth: 2,
                        '&:hover': { borderWidth: 2 }
                      }}
                    >
                      {escalating ? 'Escalating...' : 'Escalate Now'}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </CardContent>
        </Card>
        
        {dispute.escalated_at && (
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>✓ Escalation documents generated on {new Date(dispute.escalated_at).toLocaleDateString()}</strong>
              <br />
              BBB complaint, FTC report, and chargeback letter were emailed to you.
              {dispute.bbb_complaint && <><br />Check your dispute details below for copies.</>}
            </Typography>
          </Alert>
        )}

        {dispute.legal_citations && dispute.legal_citations.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>Legal Citations Used</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {dispute.legal_citations.map((citation, idx) => (
                <Chip key={idx} label={citation} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}
        
        {dispute.escalated_at && dispute.bbb_complaint && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'warning.main' }}>
              📋 Escalation Documents (Pro)
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                BBB Complaint
              </Typography>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', whiteSpace: 'pre-wrap', fontSize: '0.9rem', mb: 3, border: '1px solid', borderColor: 'divider' }}>
                {dispute.bbb_complaint}
              </Paper>
            </Box>
            
            {dispute.ftc_report && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  FTC Consumer Report
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', whiteSpace: 'pre-wrap', fontSize: '0.9rem', mb: 3, border: '1px solid', borderColor: 'divider' }}>
                  {dispute.ftc_report}
                </Paper>
              </Box>
            )}
            
            {dispute.chargeback_letter && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Chargeback Letter
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', whiteSpace: 'pre-wrap', fontSize: '0.9rem', mb: 3, border: '1px solid', borderColor: 'divider' }}>
                  {dispute.chargeback_letter}
                </Paper>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Resolution Dialog */}
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Dispute as Resolved</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Amount Recovered ($)"
              type="number"
              value={amountRecovered}
              onChange={(e) => setAmountRecovered(e.target.value)}
              placeholder={dispute?.amount_claimed ? `Original: $${parseFloat(dispute.amount_claimed).toFixed(2)}` : '0.00'}
              helperText="How much money did you recover? (Leave blank if none)"
              sx={{ mb: 2 }}
              inputProps={{ step: "0.01", min: "0" }}
            />
            <TextField
              fullWidth
              label="Resolution Notes (Optional)"
              multiline
              rows={3}
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="e.g., Full refund received, Replacement product shipped, etc."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResolveSubmit} variant="contained" color="success">
            Mark as Resolved
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Letter via Email</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
            Your letter will be sent from your email address ({userInfo?.email}) to the company.
          </Alert>
          <TextField
            fullWidth
            label="Recipient Email"
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="support@company.com"
            helperText={
              dispute?.company_contacts?.email && 
              dispute.company_contacts.email !== 'null' && 
              dispute.company_contacts.email !== null 
                ? `Auto-detected: ${dispute.company_contacts.email}` 
                : 'Enter company email address'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={sendingEmail}>Cancel</Button>
          <Button 
            onClick={handleSendEmail} 
            variant="contained" 
            color="success"
            disabled={sendingEmail || !recipientEmail}
            startIcon={sendingEmail ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Container>
  );
}

export default DisputeDetail;
